/**
 * Rapier-backed MatchWorld (ADR-0002: Rapier answers queries; the authoritative state stays in
 * plain MechState). Static box colliders come from the arena sidecar; the move-and-slide solve
 * uses Rapier's KinematicCharacterController; deployable barriers are synced in as extra boxes.
 * Mech capsules are never part of any query: mech-versus-mech contact is analytic in
 * `packages/shared/src/combat` so it stays deterministic and pose-substitutable.
 */
import RAPIER from '@dimforge/rapier3d-compat';
let initPromise = null;
/** Loads the Rapier WASM once; call before `ArenaWorld.create`. */
export function initPhysics() {
    initPromise ??= RAPIER.init();
    return initPromise;
}
const CONTROLLER_OFFSET = 0.05;
const AUTOSTEP_MIN_WIDTH = 0.3;
const MAX_SLOPE_CLIMB = (50 * Math.PI) / 180;
const MIN_SLOPE_SLIDE = (55 * Math.PI) / 180;
function yawQuaternion(yaw) {
    return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) };
}
const IDENTITY_ROTATION = { x: 0, y: 0, z: 0, w: 1 };
export class ArenaWorld {
    killY;
    world;
    controller;
    capsules = new Map();
    capsuleHandles = new Set();
    blockers = new Map();
    blockerIds = new Map();
    blockerPoses = new Map();
    ray = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 1 });
    /** The camera's probe for `castSphere`, rebuilt only when the radius changes. */
    sphere = null;
    notCapsule = (collider) => !this.capsuleHandles.has(collider.handle);
    capsuleDef = null;
    constructor(def) {
        this.killY = def.killY;
        this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
        for (const box of def.colliders) {
            const desc = RAPIER.ColliderDesc.cuboid(box.halfExtents[0], box.halfExtents[1], box.halfExtents[2])
                .setTranslation(box.center[0], box.center[1], box.center[2])
                .setFriction(0);
            const q = box.rotation
                ? { x: box.rotation[0], y: box.rotation[1], z: box.rotation[2], w: box.rotation[3] }
                : yawQuaternion(box.yaw);
            desc.setRotation(q);
            this.world.createCollider(desc);
        }
        // Static colliders only become visible to scene queries after the broad phase updates.
        this.world.step();
        this.controller = this.world.createCharacterController(CONTROLLER_OFFSET);
        this.controller.setUp({ x: 0, y: 1, z: 0 });
        this.controller.setSlideEnabled(true);
        this.controller.setMaxSlopeClimbAngle(MAX_SLOPE_CLIMB);
        this.controller.setMinSlopeSlideAngle(MIN_SLOPE_SLIDE);
        this.controller.setApplyImpulsesToDynamicBodies(false);
    }
    /** Requires `initPhysics()` to have resolved. */
    static create(def) {
        if (initPromise === null)
            throw new Error('call initPhysics() before ArenaWorld.create()');
        return new ArenaWorld(def);
    }
    moveCapsule(from, delta, capsule, out) {
        const collider = this.capsuleFor(capsule);
        const centerY = from.y + capsule.radius + capsule.halfHeight;
        collider.setTranslation({ x: from.x, y: centerY, z: from.z });
        // Mech capsules are filtered out: two mechs of one size share a collider, and mech-versus-mech
        // contact is resolved analytically by the combat rules.
        this.controller.computeColliderMovement(collider, { x: delta.x, y: delta.y, z: delta.z }, RAPIER.QueryFilterFlags.EXCLUDE_SENSORS, undefined, this.notCapsule);
        const moved = this.controller.computedMovement();
        out.x = from.x + moved.x;
        out.y = from.y + moved.y;
        out.z = from.z + moved.z;
        out.grounded = this.controller.computedGrounded();
        out.hitWall = false;
        out.hitCeiling = false;
        const n = this.controller.numComputedCollisions();
        for (let i = 0; i < n; i++) {
            const hit = this.controller.computedCollision(i);
            if (!hit)
                continue;
            const ny = hit.normal1.y;
            if (ny < -0.7)
                out.hitCeiling = true;
            else if (Math.abs(ny) < 0.3)
                out.hitWall = true;
        }
    }
    /**
     * Distance along the unit `direction` to the first arena surface or blocker, or null when
     * nothing lies within `maxDistance`. Mech capsules are ignored (camera clipping, line of sight).
     */
    castRay(origin, direction, maxDistance) {
        this.ray.origin = origin;
        this.ray.dir = direction;
        const hit = this.world.castRay(this.ray, maxDistance, true, undefined, undefined, undefined, undefined, this.notCapsule);
        return hit ? hit.timeOfImpact : null;
    }
    /**
     * Distance along the unit `direction` a sphere of `radius` can travel from `origin` before it
     * touches arena geometry or a blocker (the chase camera's clip, Phase 8); null when the whole
     * `maxDistance` is clear. Mech capsules are ignored.
     */
    castSphere(origin, direction, radius, maxDistance) {
        this.sphere ??= new RAPIER.Ball(radius);
        if (this.sphere.radius !== radius)
            this.sphere = new RAPIER.Ball(radius);
        const hit = this.world.castShape(origin, IDENTITY_ROTATION, direction, this.sphere, 0, maxDistance, true, undefined, undefined, undefined, undefined, this.notCapsule);
        return hit ? hit.time_of_impact : null;
    }
    castRayHit(origin, direction, maxDistance) {
        this.ray.origin = origin;
        this.ray.dir = direction;
        const hit = this.world.castRayAndGetNormal(this.ray, maxDistance, true, undefined, undefined, undefined, undefined, this.notCapsule);
        if (!hit)
            return null;
        return {
            distance: hit.timeOfImpact,
            normal: { x: hit.normal.x, y: hit.normal.y, z: hit.normal.z },
            blockerId: this.blockerIds.get(hit.collider.handle) ?? null,
        };
    }
    syncBlockers(blockers) {
        let changed = false;
        for (const [id, collider] of this.blockers) {
            if (blockers.some((b) => b.id === id))
                continue;
            this.blockerIds.delete(collider.handle);
            this.blockerPoses.delete(id);
            this.world.removeCollider(collider, false);
            this.blockers.delete(id);
            changed = true;
        }
        for (const b of blockers) {
            const existing = this.blockers.get(b.id);
            if (existing) {
                // A moving platform: re-pose the collider in place rather than recreating it.
                const pose = this.blockerPoses.get(b.id);
                if (pose &&
                    pose.x === b.center.x &&
                    pose.y === b.center.y &&
                    pose.z === b.center.z &&
                    pose.yaw === b.yaw) {
                    continue;
                }
                existing.setTranslation({ x: b.center.x, y: b.center.y, z: b.center.z });
                existing.setRotation(yawQuaternion(b.yaw));
                this.blockerPoses.set(b.id, { x: b.center.x, y: b.center.y, z: b.center.z, yaw: b.yaw });
                changed = true;
                continue;
            }
            const desc = RAPIER.ColliderDesc.cuboid(b.halfExtents.x, b.halfExtents.y, b.halfExtents.z)
                .setTranslation(b.center.x, b.center.y, b.center.z)
                .setRotation(yawQuaternion(b.yaw))
                .setFriction(0);
            const collider = this.world.createCollider(desc);
            this.blockers.set(b.id, collider);
            this.blockerIds.set(collider.handle, b.id);
            this.blockerPoses.set(b.id, { x: b.center.x, y: b.center.y, z: b.center.z, yaw: b.yaw });
            changed = true;
        }
        // A broad-phase refresh so queries see the new set and the new poses.
        if (changed)
            this.world.step();
    }
    /** Ids of the blockers currently in the world (for tests and debugging). */
    blockerIdList() {
        return [...this.blockers.keys()];
    }
    /** Releases the WASM-side objects. */
    free() {
        this.world.free();
        this.capsules.clear();
        this.capsuleHandles.clear();
        this.blockers.clear();
        this.blockerIds.clear();
        this.blockerPoses.clear();
    }
    capsuleFor(capsule) {
        const key = `${String(capsule.radius)}:${String(capsule.halfHeight)}`;
        let collider = this.capsules.get(key);
        if (!collider) {
            collider = this.world.createCollider(RAPIER.ColliderDesc.capsule(capsule.halfHeight, capsule.radius));
            this.capsules.set(key, collider);
            this.capsuleHandles.add(collider.handle);
        }
        if (this.capsuleDef !== capsule) {
            this.controller.enableAutostep(capsule.stepHeight, AUTOSTEP_MIN_WIDTH, false);
            this.controller.enableSnapToGround(capsule.snapDistance);
            this.capsuleDef = capsule;
        }
        return collider;
    }
}
//# sourceMappingURL=arena-world.js.map